import React from 'react'
import { Button, Input, Select } from '@/components/ds'
import { Ic } from '@/components/icons'
import { QueryState, usePageSize } from '@/components/common'
import { AddLeadDialog, ImportLeadsDialog, LeadsTable, LEADS_PAGE_SIZE } from '@/components/leads'
import { listCampaigns, listLeads } from '@/lib/db'
import { useAsync } from '@/lib/useAsync'
import { useToast } from '@/lib/toast'
import type { ScreenProps } from '@/App'

/** One-shot: set before navigating here to open the CSV import dialog. */
export const OPEN_LEAD_IMPORT = 'lm-open-lead-import'

export function LeadsScreen({ onDataChange }: ScreenProps) {
  const toast = useToast()
  const [search, setSearch] = React.useState('')
  const [debounced, setDebounced] = React.useState('')
  const [campaignId, setCampaignId] = React.useState('')
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = usePageSize('leads', LEADS_PAGE_SIZE)
  const [adding, setAdding] = React.useState(false)
  const [importing, setImporting] = React.useState(false)

  // Arriving via the Dashboard's "Import leads" link opens the dialog.
  React.useEffect(() => {
    if (sessionStorage.getItem(OPEN_LEAD_IMPORT)) {
      sessionStorage.removeItem(OPEN_LEAD_IMPORT)
      setImporting(true)
    }
  }, [])

  // Debounce so typing doesn't fire a query per keystroke.
  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setDebounced(search)
      setPage(0)
    }, 250)
    return () => window.clearTimeout(t)
  }, [search])

  const campaigns = useAsync(() => listCampaigns(), [])

  const leads = useAsync(
    () =>
      listLeads({
        campaignId: campaignId || undefined,
        search: debounced || undefined,
        limit: pageSize,
        offset: page * pageSize,
      }),
    [campaignId, debounced, page, pageSize],
  )

  const rows = leads.data?.rows ?? []
  const total = leads.data?.count ?? 0
  const campaignName = new Map((campaigns.data ?? []).map((c) => [c.id, c.name]))
  const selectedCampaign = (campaigns.data ?? []).find((c) => c.id === campaignId) ?? null

  function refresh() {
    leads.reload()
    onDataChange()
  }

  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', flex: 'none' }}
      >
        <div style={{ flex: 1, minWidth: 200, maxWidth: 340 }}>
          <Input
            prefix={<Ic.search size={14} />}
            placeholder="Search name or email…"
            value={search}
            onChange={setSearch}
          />
        </div>
        <div style={{ width: 180 }}>
          <Select
            options={[
              { value: '', label: 'All campaigns' },
              ...(campaigns.data ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
            value={campaignId}
            onChange={(v) => {
              setCampaignId(v)
              setPage(0)
            }}
          />
        </div>
        <div style={{ flex: 1 }} />
        <Button
          variant="secondary"
          icon={<Ic.upload size={14} />}
          onClick={() => setImporting(true)}
        >
          Import CSV
        </Button>
        <Button icon={<Ic.plus size={14} />} onClick={() => setAdding(true)}>
          Add lead
        </Button>
      </div>

      <QueryState
        loading={leads.loading}
        error={leads.error}
        empty={rows.length === 0}
        emptyTitle={debounced ? 'No matches' : 'No leads yet'}
        emptyDescription={
          debounced
            ? 'Try a different search, or clear the campaign filter.'
            : 'Add one by hand or import a CSV with email, name, and any merge fields.'
        }
        emptyAction={
          !debounced ? (
            <Button icon={<Ic.plus size={14} />} onClick={() => setAdding(true)}>
              Add lead
            </Button>
          ) : undefined
        }
        onRetry={leads.reload}
      >
        <LeadsTable
          rows={rows}
          total={total}
          campaign={selectedCampaign}
          campaignNames={campaignName}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPageSize(n)
            setPage(0)
          }}
          onChanged={refresh}
        />
      </QueryState>

      {adding && (
        <AddLeadDialog
          campaigns={(campaigns.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          defaultCampaignId={campaignId}
          onClose={() => setAdding(false)}
          onAdded={(message) => {
            setAdding(false)
            toast('success', 'Lead added', message)
            refresh()
          }}
        />
      )}

      {importing && (
        <ImportLeadsDialog
          campaigns={(campaigns.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          defaultCampaignId={campaignId}
          onClose={() => setImporting(false)}
          onDone={(inserted, queued, skipped) => {
            setImporting(false)
            toast(
              'success',
              `${inserted} lead${inserted === 1 ? '' : 's'} imported`,
              `${queued} queued${skipped ? ` · ${skipped} skipped` : ''}`,
            )
            refresh()
          }}
        />
      )}
    </div>
  )
}
